import { useEffect, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Button from '@mui/material/Button'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'

import type { LiveMessage } from '../hooks/useLiveUpdates'

// ToastStack surfaces critical exceptions as in-app snackbars. Drops
// duplicates when the same subject_id arrives inside a 15s window (WS
// re-broadcasts happen on severity promotion). Auto-dismisses after
// 12 seconds; the user can also dismiss manually.
//
// Deliberately only reacts to severity=critical on exception.raised /
// exception.updated messages. High/warn/info surface as list-refresh
// invalidations (via useLiveUpdates), not as toasts.
export type Toast = {
  key: string
  severity: 'error' | 'warning' | 'info' | 'success'
  code: string
  subjectSummary: string
  createdAt: number
}

const AUTO_HIDE_MS = 12_000
const DEDUP_WINDOW_MS = 15_000
const MAX_STACK = 4

export default function ToastStack({ lastMessage }: { lastMessage: LiveMessage | null }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const recentIdsRef = useRef<Map<string, number>>(new Map())

  useEffect(() => {
    if (!lastMessage) return
    if (lastMessage.kind !== 'exception.raised' && lastMessage.kind !== 'exception.updated') return

    const body = lastMessage.body as ExceptionBody | undefined
    if (!body || body.severity !== 'critical') return

    const id = `${body.subject_type}:${body.subject_id ?? body.code}`
    const now = Date.now()
    const seenAt = recentIdsRef.current.get(id) ?? 0
    if (now - seenAt < DEDUP_WINDOW_MS) return
    recentIdsRef.current.set(id, now)

    // Prune the dedup map opportunistically.
    for (const [k, v] of recentIdsRef.current) {
      if (now - v > DEDUP_WINDOW_MS * 4) recentIdsRef.current.delete(k)
    }

    setToasts((prev) => {
      const next = [...prev, {
        key: `${id}-${now}`,
        severity: 'error' as const,
        code: body.code,
        subjectSummary: body.subject_summary ?? body.subject_type,
        createdAt: now,
      }]
      // Cap the stack so we don't scroll off the screen during storms.
      return next.slice(-MAX_STACK)
    })
  }, [lastMessage])

  const dismiss = (key: string) => setToasts((prev) => prev.filter((t) => t.key !== key))

  return (
    <Snackbar
      open={toasts.length > 0}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{ top: 80 + '!important' }} // clear the AppBar
    >
      <Stack spacing={1} sx={{ width: { xs: '100vw', sm: 380 }, maxWidth: '100vw', pr: { xs: 1, sm: 0 } }}>
        {toasts.map((t) => (
          <ToastAlert
            key={t.key}
            toast={t}
            onDismiss={() => dismiss(t.key)}
          />
        ))}
      </Stack>
    </Snackbar>
  )
}

function ToastAlert({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, AUTO_HIDE_MS)
    return () => clearTimeout(t)
  }, [onDismiss])

  return (
    <Alert
      severity={toast.severity}
      variant="filled"
      onClose={onDismiss}
      action={
        <Button
          size="small"
          color="inherit"
          component={RouterLink}
          to="/"
          onClick={onDismiss}
          sx={{ textTransform: 'none' }}
        >
          Open
        </Button>
      }
    >
      <AlertTitle sx={{ mb: 0.5 }}>CRITICAL · {toast.code}</AlertTitle>
      {toast.subjectSummary}
    </Alert>
  )
}

type ExceptionBody = {
  code: string
  severity: 'info' | 'warn' | 'high' | 'critical'
  subject_type: string
  subject_id?: string
  subject_summary?: string
}
